import { APP_CONFIG } from '@/constants/app';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';

// Types spécifiques aux templates email
export interface EmailTemplateDto {
  uuid: string;
  folderUuid?: string;
  templateType: 'dsn' | 'bulletins' | 'editions';
  name: string;
  subject: string;
  htmlContent: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEmailTemplateDto {
  templateType: 'dsn' | 'bulletins' | 'editions';
  name: string;
  subject: string;
  htmlContent: string;
  isActive: boolean;
  isDefault?: boolean;
}

export interface UpdateEmailTemplateDto {
  name?: string;
  subject?: string;
  htmlContent?: string;
  isActive?: boolean;
  isDefault?: boolean;
}

export interface EmailTemplateResponse {
  data: EmailTemplateDto;
  meta: {
    timestamp: string;
    path: string;
    statusCode: number;
  };
}

export interface EmailTemplatesListResponse {
  data: EmailTemplateDto[];
  meta: {
    timestamp: string;
    path: string;
    statusCode: number;
  };
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface DefaultTemplateResponse {
  data: {
    subject: string;
    htmlContent: string;
  };
  meta: {
    timestamp: string;
    path: string;
    statusCode: number;
  };
}

export interface ActiveTemplateResponse {
  data: {
    uuid: string;
    name: string;
    subject: string;
    htmlContent: string;
    templateType: 'dsn' | 'bulletins' | 'editions';
    isDefault: boolean;
    isActive: boolean;
  };
  meta: {
    timestamp: string;
    path: string;
    statusCode: number;
  };
}

export interface RenderTemplateRequest {
  templateUuid?: string;
  variables: Record<string, string>;
}

export interface RenderTemplateResponse {
  data: {
    subject: string;
    htmlContent: string;
    templateUsed: {
      uuid: string;
      name: string;
    };
  };
  meta: {
    timestamp: string;
    path: string;
    statusCode: number;
  };
}

// Create axios instance for email templates service
const templatesApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl + '/email-templates',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Setup automatic token refresh interceptors
tokenManager.setupInterceptors(templatesApi);

export const emailTemplatesService = {
  // Lister tous les templates email
  getEmailTemplates: async (params?: {
    templateType?: 'dsn' | 'bulletins' | 'editions';
    isActive?: boolean;
    page?: number;
    limit?: number;
  }): Promise<EmailTemplatesListResponse> => {
    const queryParams = new URLSearchParams();

    if (params?.templateType) {
      queryParams.append('templateType', params.templateType);
    }
    if (params?.isActive !== undefined) {
      queryParams.append('isActive', params.isActive.toString());
    }
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }

    const queryString = queryParams.toString();
    const url = queryString ? `?${queryString}` : '';

    const response = await templatesApi.get<EmailTemplatesListResponse>(url);
    return response.data;
  },

  // Récupérer un template spécifique
  getEmailTemplate: async (
    templateUuid: string
  ): Promise<EmailTemplateResponse> => {
    const response = await templatesApi.get<EmailTemplateResponse>(
      `/${templateUuid}`
    );
    return response.data;
  },

  // Créer un nouveau template
  createEmailTemplate: async (
    templateData: CreateEmailTemplateDto
  ): Promise<EmailTemplateResponse> => {
    const response = await templatesApi.post<EmailTemplateResponse>(
      '',
      templateData
    );
    return response.data;
  },

  // Modifier un template existant
  updateEmailTemplate: async (
    templateUuid: string,
    templateData: UpdateEmailTemplateDto
  ): Promise<EmailTemplateResponse> => {
    // @ts-expect-error TODO: fix this
    templateData.templateType = undefined;
    const response = await templatesApi.patch<EmailTemplateResponse>(
      `/${templateUuid}`,
      templateData
    );
    return response.data;
  },

  // Supprimer un template
  deleteEmailTemplate: async (templateUuid: string): Promise<void> => {
    await templatesApi.delete(`/${templateUuid}`);
  },

  // Récupérer le template par défaut pour un type
  getDefaultEmailTemplate: async (
    templateType: 'dsn' | 'bulletins' | 'editions'
  ): Promise<DefaultTemplateResponse> => {
    const response = await templatesApi.get<DefaultTemplateResponse>(
      `/default/${templateType}`
    );
    return response.data;
  },

  // Récupérer le template actif pour un type
  getActiveEmailTemplate: async (
    templateType: 'dsn' | 'bulletins' | 'editions'
  ): Promise<ActiveTemplateResponse> => {
    const response = await templatesApi.get<ActiveTemplateResponse>(
      `/active/${templateType}`
    );
    return response.data;
  },

  // Rendre un template avec des variables (optionnel, pour le système d'envoi)
  renderTemplate: async (
    templateType: 'dsn' | 'bulletins' | 'editions',
    renderData: RenderTemplateRequest
  ): Promise<RenderTemplateResponse> => {
    const response = await templatesApi.post<RenderTemplateResponse>(
      `/render/${templateType}`,
      renderData
    );
    return response.data;
  },

  // Méthodes utilitaires pour le frontend

  // Récupérer tous les templates groupés par type (un seul appel API)
  getAllTemplatesGrouped: async (): Promise<{
    dsn: EmailTemplateDto[];
    bulletins: EmailTemplateDto[];
    editions: EmailTemplateDto[];
  }> => {
    const response = await emailTemplatesService.getEmailTemplates({
      limit: 200,
    });

    const grouped: {
      dsn: EmailTemplateDto[];
      bulletins: EmailTemplateDto[];
      editions: EmailTemplateDto[];
    } = { dsn: [], bulletins: [], editions: [] };

    for (const template of response.data) {
      if (template.templateType in grouped) {
        grouped[template.templateType].push(template);
      }
    }

    return grouped;
  },

  // Définir un template comme par défaut
  setAsDefault: async (
    templateUuid: string
  ): Promise<EmailTemplateResponse> => {
    return emailTemplatesService.updateEmailTemplate(templateUuid, {
      isDefault: true,
      isActive: true, // Un template par défaut doit être actif
    });
  },

  // Basculer le statut actif/inactif d'un template
  toggleActive: async (
    templateUuid: string,
    isActive: boolean
  ): Promise<EmailTemplateResponse> => {
    return emailTemplatesService.updateEmailTemplate(templateUuid, {
      isActive,
    });
  },

  // Configuration de templates par dossier

  // Récupérer la configuration de templates d'un dossier
  getFolderTemplateConfig: async (
    folderUuid: string
  ): Promise<{
    dsn?: string;
    bulletins?: string;
    editions?: string;
  }> => {
    try {
      const response = await templatesApi.get<{
        data: {
          dsn?: string;
          bulletins?: string;
          editions?: string;
        };
      }>(`/folders/${folderUuid}/template-config`);
      return response.data.data;
    } catch (cause) {
      const error = cause as HttpApiError;
      // Si la configuration n'existe pas encore, retourner un objet vide
      if (error.response?.status === 404) {
        return {};
      }
      throw error;
    }
  },

  // Sauvegarder la configuration de templates d'un dossier
  saveFolderTemplateConfig: async (
    folderUuid: string,
    config: {
      dsn?: string;
      bulletins?: string;
      editions?: string;
    }
  ): Promise<void> => {
    await templatesApi.post(`/folders/${folderUuid}/template-config`, {
      dsn: (config.dsn ?? '').trim() === '' ? undefined : config.dsn,
      bulletins:
        (config.bulletins ?? '').trim() === '' ? undefined : config.bulletins,
      editions:
        (config.editions ?? '').trim() === '' ? undefined : config.editions,
    });
  },
};

// Export des types pour utilisation dans les composants
export type {
  EmailTemplateDto as EmailTemplate,
  CreateEmailTemplateDto as EmailTemplateFormData,
};
